// @flow
import React, { Fragment } from 'react';
import ContentText from 'Components/ContentText';
import Check from 'Components/Check';
import privacyModel from 'Stores/PrivacyModel';
import { deleteCookie } from 'Utils';
import { getGivenBackground } from 'Utils/getColor';
import CookieTable from './table';
import styles from './styles.scss';
import IntroText from 'Components/IntroText';
import Link from 'Components/Link';
import Footnotes from 'Plugins/Footnotes';
import { useAnchor } from 'Utils/useAnchor';
import { withRouter } from 'react-router-dom';
import { InfoTextForGoogleCookies } from './InfoTextForGoogleCookies';
import { InfoTextForMatelsoCookies } from './InfoTextForMatelsoCookies';

export const COOKIE_PERFORMANCE = 'performance';
export const COOKIE_KOMFORT = 'komfort';
export const COOKIE_MARKETING = 'marketing';

type State = {
  performance: boolean,
  komfort: boolean,
  marketing: boolean,
};

class CookieSettings extends React.Component<null, State> {
  constructor(props) {
    super(props);

    this.state = {
      performance: privacyModel.cookieTypes.performance,
      komfort: privacyModel.cookieTypes.komfort,
      marketing: privacyModel.cookieTypes.marketing,
    };
  }

  handleAnchor = useAnchor(this.props);

  componentDidMount() {
    document.addEventListener('click', this.handleAnchor);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleAnchor);
  }

  setCookieWasUpdated = () => this.props.setSharedState('cookieUpdated', true);

  handlePerformanceChange = (enabled: boolean) => {
    this.setCookieWasUpdated();
    privacyModel.updateCookie(COOKIE_PERFORMANCE, enabled);

    if (!enabled) {
      // remove all analytics cookies when disabled by the user
      deleteCookie('_ga');
      deleteCookie('_gat');
      deleteCookie('_gid');
    }

    this.setState({ performance: enabled });
  };

  handleKomfortChange = (enabled: boolean) => {
    this.setCookieWasUpdated();
    privacyModel.updateCookie(COOKIE_KOMFORT, enabled);

    this.setState({ komfort: enabled });
  };

  handleMarketingChange = (enabled: boolean) => {
    this.setCookieWasUpdated();
    privacyModel.updateCookie(COOKIE_MARKETING, enabled);

    this.setState({ marketing: enabled });
  };

  render() {
    const { performance, komfort, marketing } = this.state;

    return (
      <Fragment>
        <ContentText background={getGivenBackground('grey')}>
          <h3 className={`h4 ${styles.marginBottomOneRow}`}>Notwendig</h3>
          <p>
            <Link className={styles.anchor} to="#notwendig" label="Diese Cookies" /> helfen dabei,
            unsere Webseite nutzbar zu machen, indem sie zum Beispiel Grundfunktionen wie
            Seitennavigation und Zugriff auf sichere Bereiche der Webseite ermöglichen oder den
            Status Ihrer Cookie-Einstellungen speichern. Unsere Webseite kann ohne diese Cookies
            nicht richtig funktionieren. Der Einsatz von technisch erforderlichen Cookies und
            ähnlichen Technologien ist unbedingt erforderlich, um die von Ihnen aufgerufene Website
            ordnungsgemäß und sicher mit den für diesen Dienst typischen Funktionalitäten zur
            Verfügung zu stellen. Diese Technologien werden beispielsweise genutzt, um
          </p>
          <ul>
            <li>Grundfunktionen der Webseite zu ermöglichen,</li>
            <li>die Einstellung Ihrer Datenschutzpräferenzen zu speichern,</li>
            <li>das Ausfüllen von Formularen zu ermöglichen.</li>
          </ul>
        </ContentText>
        <ContentText background={getGivenBackground('white')}>
          <h3 className={`h4 ${styles.marginBottomOneRow}`}>Komfort</h3>
          <p className={styles.marginBottomTwoRows}>
            Wir nutzen{' '}
            <Link
              className={styles.anchor}
              to="#komfort"
              label="diese Cookies und andere Technologien"
            />
            , um Ihnen die Bedienung der Seite zu erleichtern. Zum Beispiel wird der Standort des
            jeweiligen Händlers in einer Karte dargestellt.
          </p>

          <Check
            name="komfort"
            label={'Aktuell werden die Komfort-Cookies genutzt.'}
            checked={komfort}
            onChange={this.handleKomfortChange}
          />
        </ContentText>
        <ContentText background={getGivenBackground('grey')}>
          <h3 className={`h4 ${styles.marginBottomOneRow}`}>Analyse / Statistik</h3>
          <p className={styles.marginBottomTwoRows}>
            Um unser Angebot und unsere Webseite weiter zu verbessern, erfassen wir anonymisierte
            Daten für Statistiken und Analysen. Mithilfe{' '}
            <Link className={styles.anchor} to="#statistik" label="dieser Cookies" /> bzw. digitalen
            Identifikatoren (Nutzer-IDs) können wir beispielsweise die Besucherzahlen und den Effekt
            bestimmter Seiten unseres Web-Auftritts ermitteln und unsere Inhalte optimieren.
          </p>
          <Check
            name="performance"
            label={'Aktuell werden die Analyse- und Statistik-Cookies genutzt.'}
            checked={performance}
            onChange={this.handlePerformanceChange}
          />
        </ContentText>
        <ContentText background={getGivenBackground('white')}>
          <h3 className={`h4 ${styles.marginBottomOneRow}`}>Marketing &amp; Personalisierung</h3>
          <p className={styles.marginBottomTwoRows}>
            <Link className={styles.anchor} to="#marketing" label="Diese Cookies" /> und digitalen
            Identifikatoren (Nutzer-IDs) werden genutzt, um Ihnen auf {window.dealer.domain} und
            auch anderen Websites und Plattformen personalisierte Inhalte und relevante
            Werbekampagnen, passend zu Ihren Interessen, anzuzeigen. Dabei können personenbezogene
            Daten pseudonymisiert an unsere Partner übermittelt werden, um das digitale Erlebnis
            plattformübergreifend im Open Web, Social Media (Meta, Google, Amazon) und/oder{' '}
            <span lang="en">Connected Devices</span> / TV (inkl. Ausgewählte Streamingdienste) zu
            optimieren.
          </p>

          <Check
            name="marketing"
            label={'Aktuell werden die Marketing und Personalisierung-Cookies genutzt.'}
            checked={marketing}
            onChange={this.handleMarketingChange}
          />
        </ContentText>
        <IntroText
          headline={{ level: 'h2', forceUppercase: true, value: 'Unsere Datenschutzrichtlinie' }}
          bodyText="<p>Hier finden Sie unsere aktuelle Datenschutzerklärung sowie alle archivierten Versionen.</p>"
          cta1={{
            href: '/datenschutz/',
            label: 'Jetzt ansehen',
            buttonVariant: 'secondary',
            screenReaderLabel: 'Unsere Datenschutzrichtlinie',
          }}
          background={getGivenBackground('grey')}
        />
        <CookieTable type="notwendig" anchor="notwendig" background={getGivenBackground('white')} />
        <ContentText background={getGivenBackground('grey')}>
          <h4 className={`h5 ${styles.marginBottomOneRow}`}>
            Des Weiteren setzen wir folgende Technologie ein:
          </h4>
          <p>
            <span lang="en">Google Tag Manager</span>: <span lang="en">Google Tag Manager</span> ist
            eine Lösung von <span lang="en">Google Ireland Limited</span>, Google Building Gordon
            House, 4 Barrow St, Dublin, D04 E5W5, Irland ("Google"), mit der wir sog. Website-
            <span lang="en">Tags</span> über eine Oberfläche verwalten und so andere Dienste in
            unser Onlineangebot einbinden können. So kann der{' '}
            <span lang="en">Google Tag Manager</span> erkennen, ob für bestimmte Cookies eine
            Einwilligung vorliegt oder nicht und diese dann im Bedarfsfall aktivieren oder
            deaktivieren. Mit <span lang="en">Google Tag Manager</span> selbst, welches die{' '}
            <span lang="en">Tags</span> implementiert, werden noch keine Auswertungen vorgenommen,
            wie das Erstellen von Nutzerprofilen oder das Speichern von weiteren Cookies. Google
            erfährt nur die IP-Adresse des Nutzers, was notwendig ist, um den{' '}
            <span lang="en">Google Tag Manager</span> auszuführen und vom Nutzer gewünschte
            Einstellungen umzusetzen. Die IP-Adresse wird von Google anonymisiert. Weitere
            Informationen zum Datenschutz bei Google finden sich unter{' '}
            <Link
              to="https://marketingplatform.google.com"
              label="marketingplatform.google.com"
              target="_blank"
              isExternal
            />{' '}
            und in der{' '}
            <Link
              to="https://policies.google.com/privacy"
              label="Datenschutzerklärung"
              target="_blank"
              isExternal
            />
            .
          </p>
        </ContentText>
        <CookieTable type="komfort" anchor="komfort" background={getGivenBackground('white')} />
        <CookieTable type="statistik" anchor="statistik" background={getGivenBackground('grey')}>
          <InfoTextForGoogleCookies type="statistik" />
        </CookieTable>
        <CookieTable type="marketing" anchor="marketing" background={getGivenBackground('white')}>
          <InfoTextForGoogleCookies type="marketing" />
          <InfoTextForMatelsoCookies type="marketing" />
        </CookieTable>
        <Footnotes
          background={getGivenBackground('grey')}
          sharedState={this.props.sharedState}
          idAttribute="cookie-footnotes"
          footnotes={[
            {
              id: 'C*',
              refId: 'c-asterisk',
              number: '*',
              text: `
                <p>
                  <em lang="en">First Party-Cookies</em> stammen vom Betreiber der Webseite.
                  Diese Cookies dienen dazu, den Besucher wieder zu erkennen und ermöglichen aussagekräftige
                  Berichte zur Nutzung der Webseite, diese den Nutzerbedürfnissen anzupassen und / oder Marketing-Strategien zu erarbeiten.
                  Diese Daten werden nicht domain-übergreifend zugänglich gemacht.
                </p>
              `,
            },
            {
              id: 'C**',
              refId: 'c-asterisk-asterisk',
              number: '**',
              text: `
                <p>
                  <em lang="en">Third Party-Cookies</em> stammen von Werbetreibenden und werden nicht durch den Betreiber der Webseite gesetzt.
                  Diese Cookies ermöglichen die Wiedererkennung eines Webseiten-Besuchers, das Sammeln von Informationen für Werbung,
                  benutzerdefinierte Inhalte sowie Statistiken.
                </p>
              `,
            },
          ]}
        />
      </Fragment>
    );
  }
}

export default withRouter(CookieSettings);
